import { type ReactNode } from "react";
import { type Row, type Table } from "@tanstack/react-table";
import { Loader2 } from "lucide-react";

import { SECTION_V5 } from "./design-tokens";
import { SectionDataTable } from "./SectionDataTable";
import { type SectionCardContext } from "./section-types";
import { type SectionView } from "./SectionViewSwitcher";

interface SectionContentProps<TRow extends { id: string }> {
    isError?: boolean;
    isLoading?: boolean;
    hasRows: boolean;
    rows: Row<TRow>[];
    table: Table<TRow>;
    effectiveView: SectionView;
    renderCard: (row: TRow, ctx: SectionCardContext) => ReactNode;
    renderGridTile?: (row: TRow, ctx: SectionCardContext) => ReactNode;
    cardContextFor: (row: TRow) => SectionCardContext;
    onRowClick?: (row: TRow) => void;
    getRowClassName?: (row: TRow) => string | undefined;
    emptyState?: ReactNode;
    errorState?: ReactNode;
}

/** Body of `SectionDataView`: error / loading / empty states, then table, grid, or cards. */
export function SectionContent<TRow extends { id: string }>({
    isError,
    isLoading,
    hasRows,
    rows,
    table,
    effectiveView,
    renderCard,
    renderGridTile,
    cardContextFor,
    onRowClick,
    getRowClassName,
    emptyState,
    errorState,
}: SectionContentProps<TRow>) {
    if (isError) {
        return <div className={SECTION_V5.emptyState}>{errorState}</div>;
    }
    if (isLoading) {
        return (
            <div className={SECTION_V5.emptyState}>
                <Loader2 className="size-5 animate-spin text-muted-foreground" aria-hidden />
            </div>
        );
    }
    if (!hasRows) {
        return <div className={SECTION_V5.emptyState}>{emptyState}</div>;
    }
    if (effectiveView === "table") {
        return <SectionDataTable table={table} onRowClick={onRowClick} getRowClassName={getRowClassName} />;
    }
    if (effectiveView === "grid" && renderGridTile) {
        return (
            <div className={SECTION_V5.gridDense}>
                {rows.map((row) => (
                    <div key={row.id}>{renderGridTile(row.original, cardContextFor(row.original))}</div>
                ))}
            </div>
        );
    }
    return (
        <ul className={SECTION_V5.cardsGrid}>
            {rows.map((row) => (
                <li key={row.id}>{renderCard(row.original, cardContextFor(row.original))}</li>
            ))}
        </ul>
    );
}
